import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Globe, Info, Landmark, Mail, MapPin, Megaphone, MessageCircle, Phone, Share2, Video } from 'lucide-react';
import { getCategory, getCategoryBySlug } from '../config/categories';
import { getArea } from '../config/areas';
import { mapPointOf, navTargetOf } from '../utils/geo';
import { useListing, useListings } from '../hooks/useListings';
import { useAuth } from '../hooks/useAuth';
import { pageAccess } from '../utils/listingPreview';
import PreviewBanner from '../components/directory/PreviewBanner';
import { useOpeningHoursMap } from '../hooks/useDirectorySearch';
import { ISRAEL_TIME_ZONE } from '../utils/israelTime';
import {
  activeMessages,
  displayAddress,
  pickLang,
  primaryPhone,
  subcategoryLabel,
  telLink,
  whatsappLink,
} from '../utils/listingDisplay';
import { useShiurWeek } from '../hooks/useShiurim';
import ShiurWeek from '../components/directory/ShiurWeek';
import { isEmergency } from '../utils/directorySearch';
import CategoryIcon from '../components/directory/CategoryIcon';
import ListingCard, { listingMapHref } from '../components/directory/ListingCard';
import ListingDetailsBlock from '../components/directory/ListingDetailsBlock';
import WeekHours from '../components/directory/WeekHours';
import NavigateButton from '../components/directory/NavigateButton';
import LazyDirectoryMap from '../components/directory/map/LazyDirectoryMap';
import { OpenStatusBadge } from '../components/directory/StatusBadge';
import { breadcrumbJsonLd, pageUrl, placeJsonLd, schemaTypeForCategory, shortDescription } from '../utils/seo';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold text-ink">{title}</h2>
      {children}
    </section>
  );
}

const actionClass =
  'inline-flex items-center justify-center gap-2 rounded-full min-h-11 px-4 text-sm font-medium transition-colors';

/** /directory/:slug/:listingId (plan 8.4). */
export default function DirectoryListingPage() {
  const { slug = '', listingId = '' } = useParams<{ slug: string; listingId: string }>();
  // Shuls have one true address (plan 8.2).
  if (slug === 'shuls') return <Navigate to={`/shul/${listingId}`} replace />;
  return <ListingDetail slug={slug} listingId={listingId} />;
}

function ListingDetail({ slug, listingId }: { slug: string; listingId: string }) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const { listing: readListing, loading: listingLoading, error } = useListing(listingId);
  const { user, loading: authLoading } = useAuth();
  // Published = everyone; not published = owners + super admin see a preview (plan 16).
  const waitingForAuth = !!readListing && readListing.status !== 'published' && authLoading;
  const access = readListing && !waitingForAuth ? pageAccess(readListing, user) : null;
  const listing = access === 'public' || access === 'preview' ? readListing : null;
  const isPreview = access === 'preview';
  const loading = listingLoading || waitingForAuth;
  const listingArray = useMemo(() => (listing ? [listing] : []), [listing]);
  const hoursById = useOpeningHoursMap(listingArray);
  const [copied, setCopied] = useState(false);
  // Shiurim: the shul it is held in (for the link and its minyan-tied times) and its week.
  const shiurDetails = listing?.category === 'shiur' ? listing.details : undefined;
  const { listing: shiurShulListing } = useListing(shiurDetails?.where === 'shul' ? shiurDetails.shulId ?? '' : '');
  const shiurShul = shiurShulListing?.category === 'shul' ? shiurShulListing : null;
  const shiurSessions = useMemo(() => shiurDetails?.sessions ?? [], [shiurDetails?.sessions]);
  const shiurWeek = useShiurWeek(shiurSessions, shiurShul);
  // Head tags + structured data (docs/seo-plan.md). Only published listings go to Google.
  const seoLang = isHebrew ? 'he' : 'en';
  const seoNotFound = !loading && !error && !listing;
  const seoName = listing ? pickLang(listing.nameEn, listing.nameHe, isHebrew) : '';
  const seoConfig = listing && listing.category !== 'shul' ? getCategory(listing.category) : null;
  const seoPath = seoConfig && listing ? `/directory/${seoConfig.slug}/${listing.id}` : '';
  const seoText = listing ? pickLang(listing.descriptionEn, listing.descriptionHe, isHebrew) : '';
  const seoPoint = listing ? mapPointOf(listing.location) : null;
  usePageTitle(
    listing ? seoName : seoNotFound ? t('directory.detail.notFound') : undefined,
    listing && seoConfig && !isPreview
      ? {
          description: seoText
            ? shortDescription(`${seoName} – ${seoText}`)
            : t('seo.desc.listing', { name: seoName, category: t(`directory.category.${listing.category}`) }),
          image: listing.photoUrl,
          jsonLd: [
            placeJsonLd({
              type: schemaTypeForCategory(listing.category),
              name: seoName,
              otherName: isHebrew ? listing.nameEn : listing.nameHe,
              description: seoText,
              url: pageUrl(seoPath, seoLang),
              lang: seoLang,
              address: displayAddress(listing, isHebrew),
              lat: seoPoint && !seoPoint.approximate ? seoPoint.lat : undefined,
              lng: seoPoint && !seoPoint.approximate ? seoPoint.lng : undefined,
              phone: primaryPhone(listing),
              image: listing.photoUrl,
            }),
            breadcrumbJsonLd([
              { name: t('seo.breadcrumbHome'), url: pageUrl('/', seoLang) },
              { name: t(`directory.categoryPlural.${listing.category}`), url: pageUrl(`/directory/${seoConfig.slug}`, seoLang) },
              { name: seoName, url: pageUrl(seoPath, seoLang) },
            ]),
          ],
        }
      : { noindex: seoNotFound || isPreview }
  );

  // "More from this owner": published listings sharing an owner (filtered in the browser, no extra index).
  const hasOwners = (listing?.ownerUids?.length ?? 0) > 0;
  const { listings: published } = useListings({ skip: !hasOwners });
  const moreFromOwner = useMemo(
    () =>
      listing && hasOwners
        ? published.filter(
            (l) => l.id !== listing.id && l.ownerUids.some((uid) => listing.ownerUids.includes(uid))
          )
        : [],
    [published, listing, hasOwners]
  );
  const moreHours = useOpeningHoursMap(moreFromOwner);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-3 animate-pulse">
        <div className="h-8 w-2/3 rounded-[10px] bg-fog" />
        <div className="h-4 w-1/2 rounded bg-fog" />
        <div className="h-32 rounded-[16px] bg-fog" />
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center space-y-4">
        <p className="text-ink">{error ? t('common.error') : t('directory.detail.notFound')}</p>
        <Link to="/directory" className="text-sm text-ink underline">
          {t('directory.detail.backToAll')}
        </Link>
      </div>
    );
  }

  if (listing.category === 'shul') return <Navigate to={`/shul/${listing.id}`} replace />;
  const config = getCategory(listing.category);
  if (getCategoryBySlug(slug)?.key !== listing.category) {
    return <Navigate to={`/directory/${config.slug}/${listing.id}`} replace />;
  }

  const name = pickLang(listing.nameEn, listing.nameHe, isHebrew);
  const address = displayAddress(listing, isHebrew);
  const directions = pickLang(listing.location?.directionsNoteEn, listing.location?.directionsNoteHe, isHebrew);
  const note = pickLang(listing.noteEn, listing.noteHe, isHebrew);
  const description = pickLang(listing.descriptionEn, listing.descriptionHe, isHebrew);
  const messages = activeMessages(listing.messages, new Date().toISOString());
  const phone = primaryPhone(listing);
  const whatsapp = listing.contacts?.find((c) => c.whatsapp)?.whatsapp;
  const hours = hoursById[listing.id];
  const emergency = isEmergency(listing);
  const area = getArea(listing.location?.areaId ?? '');
  const navTarget = navTargetOf(listing.location, area, isHebrew);
  const mapPoint = mapPointOf(listing.location);
  const lastUpdated = listing.updatedAt
    ? new Intl.DateTimeFormat(isHebrew ? 'he-IL' : 'en-GB', { timeZone: ISRAEL_TIME_ZONE, dateStyle: 'medium' }).format(
        new Date(listing.updatedAt)
      )
    : '';

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: name, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Share sheet closed or clipboard blocked: nothing to do.
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      {isPreview && (
        <PreviewBanner listingId={listing.id} status={listing.status} rejectionReason={listing.rejectionReason} />
      )}
      {/* Header */}
      <div className="space-y-3">
        <Link to={`/directory/${config.slug}`} className="inline-flex items-center gap-1.5 text-sm text-ash hover:text-ink">
          <CategoryIcon category={listing.category} className="w-4 h-4" />
          {t(`directory.categoryPlural.${listing.category}`)}
        </Link>
        {listing.photoUrl && (
          <img src={listing.photoUrl} alt="" className="w-full max-h-64 object-cover rounded-[16px] border border-ink/10" />
        )}
        <h1 className="text-2xl font-bold text-ink">{name}</h1>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs px-2 py-0.5 rounded-[10px] bg-fog text-ink font-medium">
            {subcategoryLabel(listing, t, isHebrew)}
          </span>
          <OpenStatusBadge listing={listing} result={hours} />
        </div>
        {shiurShul && (
          <div className="flex items-start gap-1.5 text-sm text-ash">
            <Landmark className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
            <span>
              <Link to={`/shul/${shiurShul.id}`} className="text-ink underline">
                {t('directory.shiur.show.inShul', { shul: pickLang(shiurShul.nameEn, shiurShul.nameHe, isHebrew) })}
              </Link>
              {shiurDetails && pickLang(shiurDetails.roomEn, shiurDetails.roomHe, isHebrew) && (
                <span> · {pickLang(shiurDetails.roomEn, shiurDetails.roomHe, isHebrew)}</span>
              )}
            </span>
          </div>
        )}
        {shiurDetails?.where === 'online' && (
          <div className="flex items-center gap-1.5 text-sm text-ash">
            <Video className="w-4 h-4 shrink-0" aria-hidden />
            {t('directory.shiur.show.online')}
          </div>
        )}
        {address && shiurDetails?.where !== 'online' && (
          <div className="flex items-start gap-1.5 text-sm text-ash">
            <MapPin className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
            <span>
              {/* Like the shul page: the address opens the map with this listing's card open. */}
              {mapPoint ? (
                <Link to={listingMapHref(listing)} className="text-ink hover:opacity-70 transition-opacity" title={t('shul.showOnMap')}>
                  {address}
                </Link>
              ) : (
                address
              )}
              {listing.location?.showExactLocation === false && ` (${t('directory.detail.approxLocation')})`}
              {directions && <span className="block">{directions}</span>}
            </span>
          </div>
        )}
      </div>

      {/* Active messages */}
      {messages.length > 0 && (
        <Section title={t('directory.detail.messages')}>
          <ul className="space-y-2">
            {messages.map((m) => (
              <li key={m.id} className="flex gap-2 rounded-[16px] bg-weekday-50 text-weekday-700 p-3 text-sm">
                <Megaphone className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
                <span>{pickLang(m.textEn, m.textHe, isHebrew)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Highlighted note */}
      {note && (
        <p className="flex gap-2 rounded-[16px] bg-gold-50 text-gold-700 p-3 text-sm font-medium">
          <Info className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
          {note}
        </p>
      )}

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2">
        {phone && (
          <a
            href={telLink(phone)}
            className={`${actionClass} ${emergency ? 'bg-danger-600 hover:bg-danger-700' : 'bg-ink hover:bg-ink/85'} text-paper`}
          >
            <Phone className="w-4 h-4" aria-hidden />
            {t('directory.action.call')}
          </a>
        )}
        {whatsapp && (
          <a href={whatsappLink(whatsapp)} target="_blank" rel="noopener noreferrer" className={`${actionClass} bg-fog text-ink hover:bg-ink/10`}>
            <MessageCircle className="w-4 h-4" aria-hidden />
            {t('directory.action.whatsapp')}
          </a>
        )}
        {shiurDetails?.onlineUrl && (
          <a
            href={shiurDetails.onlineUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`${actionClass} bg-ink hover:bg-ink/85 text-paper`}
          >
            <Video className="w-4 h-4" aria-hidden />
            {t('directory.shiur.show.joinOnline')}
          </a>
        )}
        {shiurDetails?.where !== 'online' && <NavigateButton target={navTarget} />}
        <button type="button" onClick={share} className={`${actionClass} bg-fog text-ink hover:bg-ink/10`}>
          <Share2 className="w-4 h-4" aria-hidden />
          {copied ? t('directory.action.linkCopied') : t('directory.action.share')}
        </button>
      </div>

      {/* Hours */}
      {/* Always shown for categories with hours: the week, or how to reach them instead (plan 16). */}
      {config.usesHours && (
        <Section title={t('directory.detail.openingHours')}>
          {listing.hoursMode === 'scheduled' && listing.hours.length > 0 ? (
            hours && <WeekHours week={hours.week} />
          ) : (
            <div className="flex flex-wrap items-center gap-3 rounded-[16px] bg-fog p-3">
              <p className="text-sm text-ink font-medium flex-1 min-w-[12rem]">
                {t(`directory.detail.hoursNote.${listing.hoursMode === 'scheduled' ? 'noHours' : listing.hoursMode}`)}
              </p>
              {phone && listing.hoursMode !== 'alwaysOpen' && (
                <a href={telLink(phone)} className={`${actionClass} bg-ink hover:bg-ink/85 text-paper`}>
                  <Phone className="w-4 h-4" aria-hidden />
                  {t('directory.action.call')}
                </a>
              )}
            </div>
          )}
        </Section>
      )}

      {/* Shiur times */}
      {listing.category === 'shiur' && (
        <Section title={t('directory.shiur.show.weekTitle')}>
          {shiurSessions.length > 0 ? (
            <ShiurWeek days={shiurWeek} />
          ) : (
            <p className="text-sm text-ash">{t('directory.shiur.show.noTimes')}</p>
          )}
        </Section>
      )}

      {/* Category details */}
      <ListingDetailsBlock listing={listing} title={t('directory.detail.details')} />

      {/* Small map */}
      {mapPoint && (
        <Section title={t('directory.map.location')}>
          <LazyDirectoryMap
            variant="mini"
            areaId={listing.location.areaId}
            items={[{ id: listing.id, ...mapPoint, category: listing.category, title: name }]}
            className="h-48 w-full rounded-[16px] overflow-hidden border border-ink/10 z-0"
          />
          {mapPoint.approximate && <p className="text-xs text-ash">{t('directory.detail.approxLocation')}</p>}
        </Section>
      )}

      {/* Contacts */}
      {listing.contacts?.length > 0 && (
        <Section title={t('directory.detail.contacts')}>
          <ul className="space-y-3">
            {listing.contacts.map((c) => {
              const contactName = pickLang(c.nameEn, c.nameHe, isHebrew);
              return (
                <li key={c.id} className="text-sm space-y-1">
                  {contactName && <p className="font-medium text-ink">{contactName}</p>}
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {c.phone && (
                      <a href={telLink(c.phone)} className="inline-flex items-center gap-1.5 text-ink underline min-h-11">
                        <Phone className="w-4 h-4" aria-hidden />
                        <span dir="ltr">{c.phone}</span>
                      </a>
                    )}
                    {c.whatsapp && (
                      <a href={whatsappLink(c.whatsapp)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-ink underline min-h-11">
                        <MessageCircle className="w-4 h-4" aria-hidden />
                        {t('directory.action.whatsapp')}
                      </a>
                    )}
                    {c.email && (
                      <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 text-ink underline min-h-11 break-all">
                        <Mail className="w-4 h-4" aria-hidden />
                        {c.email}
                      </a>
                    )}
                    {c.website && (
                      <a href={c.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-ink underline min-h-11">
                        <Globe className="w-4 h-4" aria-hidden />
                        {t('directory.action.website')}
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {/* Description */}
      {description && (
        <Section title={t('directory.detail.about')}>
          <p className="text-sm text-ink whitespace-pre-line">{description}</p>
        </Section>
      )}

      {lastUpdated && <p className="text-xs text-ash">{t('directory.detail.lastUpdatedOn', { date: lastUpdated })}</p>}
      {listing.status === 'published' && (
        <Link
          to={`/contact?topic=listingQuestion&listing=${encodeURIComponent(listing.id)}`}
          className="inline-flex items-center gap-1.5 text-sm text-ink underline min-h-[44px]"
        >
          <Mail className="w-4 h-4" aria-hidden />
          {t('contact.aboutThisListing')}
        </Link>
      )}

      {/* More from this owner */}
      {moreFromOwner.length > 0 && (
        <Section title={t('directory.detail.moreFromOwner')}>
          <ul className="space-y-3">
            {moreFromOwner.map((l) => (
              <li key={l.id}>
                <ListingCard listing={l} hours={moreHours[l.id]} />
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

